import React, { useEffect, useRef, useState } from "react";
import useEventsData from "@/src/useData/useEventsData";
import { handlePress } from "@/src/helpers";
import S from "./LatestEvents.styled";
import {
  Button,
  Desktop,
  Icon,
  Mobile,
  TextButton,
  Wysiwyg,
} from "@/styles/components";
import Arrow from "@/assets/arrow.svg";
import { IWPImage, IWPLink } from "@/src/types";
import { motion } from "framer-motion";
import { Flex } from "@/styles/global";
import { H3 } from "@/styles/typography";

type Events = {
  title: string;
  uri: string;
  date: string;
  eventsInfo: {
    eventDate: string;
    location: string;
    featuredImage: IWPImage;
    staffOnSite: string;
    content: string;
  };
};

export interface LatestEventsProps {
  blockId?: string;
  title: string;
  link?: IWPLink;
}

const LatestEvents: React.FC<LatestEventsProps> = ({
  title,
  link,
  blockId = "",
}) => {
  const eventsWrapper = useRef<HTMLDivElement | null>(null);
  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [latestEvents, setLatestEvents] = useState<Events[]>([]);

  const events = useEventsData();
  const [currentPage, setCurrentPage] = useState(0);
  const [scrollState, setScrollState] = useState({
    isAtStart: true,
    isAtEnd: false,
  });
  const itemsPerPage = 3;
  const totalPages = Math.min(Math.ceil(events.length / itemsPerPage), 3);

  useEffect(() => {
    const latest = events.slice(0, itemsPerPage * totalPages);
    setLatestEvents(latest);
  }, [events]);

  useEffect(() => {
    if (!windowWidth) setWindowWidth(window.innerWidth);

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const checkScrollability = () => {
    if (eventsWrapper.current) {
      const { scrollLeft, scrollWidth, clientWidth } = eventsWrapper.current;
      setScrollState({
        isAtStart: scrollLeft === 0,
        isAtEnd: scrollLeft + clientWidth >= scrollWidth,
      });
    }
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener("resize", checkScrollability);
    eventsWrapper.current?.addEventListener("scroll", checkScrollability);

    return () => {
      window.removeEventListener("resize", checkScrollability);
      eventsWrapper.current?.removeEventListener("scroll", checkScrollability);
    };
  }, [latestEvents]);

  const handleNavigation = (left?: boolean) => {
    if (!eventsWrapper.current) return;
    handlePress(eventsWrapper.current, left ? -332 : 332);
    setCurrentPage((prev) =>
      left ? Math.max(0, prev - 1) : Math.min(totalPages - 1, prev + 1)
    );
  };

  const NavigationButton = ({ left }: { left?: boolean }) => (
    <S.NavigationButton
      $disabled={
        windowWidth > 1200
          ? left
            ? currentPage === 0
            : currentPage === totalPages - 1
          : left
            ? scrollState.isAtStart
            : scrollState.isAtEnd
      }
      onClick={() => handleNavigation(left)}
    >
      <Icon src={left ? "ArrowLeft" : "ArrowRight"} width={24} />
    </S.NavigationButton>
  );

  const renderCard = (events: Events, key: string) => {
    const {
      title,
      uri,
      eventsInfo: { location, eventDate, content },
    } = events;
    return (
      <S.Card to={uri} key={key}>
        <S.CardTextWrapper>
          <Flex $column $gap={8}>
            <Flex $gap={16}>
              {eventDate && <S.CardDate>{eventDate}</S.CardDate>}
              {location && <S.CardDate>{location}</S.CardDate>}
              {!eventDate && !location && <S.CardDate>Event</S.CardDate>}
            </Flex>
            <H3>{title}</H3>
          </Flex>
          <S.ContentWrapper>
            <Wysiwyg size={16} color="textBlack" html={content} />
            <Button label="Read more" url={uri} />
          </S.ContentWrapper>
          <S.CardReadMoreWrapper>
            <S.CardReadMore>Read more</S.CardReadMore>
            <Arrow />
          </S.CardReadMoreWrapper>
        </S.CardTextWrapper>
      </S.Card>
    );
  };

  const paginatedEvents = latestEvents
    .slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage)
    .map((eventsItem, index) =>
      renderCard(eventsItem, `events-${index + title}`)
    );

  return (
    <S.Section id={blockId}>
      <S.Inner>
        <S.Title id="Events">{title}</S.Title>
        <Flex $gap={16} $desktopGap={24}>
          {link && <TextButton wpLink={link} color="black" underlined />}
          {latestEvents.length > 2 && (
            <S.NavigationWrapper>
              <NavigationButton left />
              <NavigationButton />
            </S.NavigationWrapper>
          )}
        </Flex>
      </S.Inner>
      <Desktop breakpoint="xl">
        <S.Cards>
          <S.Container
            as={motion.div}
            key={currentPage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, type: "spring" }}
          >
            {paginatedEvents}
          </S.Container>
        </S.Cards>
      </Desktop>
      <Mobile breakpoint="xl">
        <S.Cards ref={eventsWrapper}>
          <S.Container>
            {latestEvents.map((eventsItem, index) =>
              renderCard(eventsItem, `events-${index + title}`)
            )}
          </S.Container>
        </S.Cards>
      </Mobile>
    </S.Section>
  );
};

export default LatestEvents;
